import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Icon, Image } from 'semantic-ui-react';
import { iRootState, iRootDispatch } from '../../store';
import { CurrentPageState, CurrentToggledMenuState } from '../../store/types/temp-types';
import { menuOptions } from '../../services/menu';
import { MenuItemProps, SubMenuItemProps } from '../../containers/types';
import MobileMenuCategory from './MobileMenuCategory';
import MobileSubmenu from './MobileSubmenu';
import {
  Overlay,
  MenuContainer,
  MenuHeader,
  LogoContainer,
  CloseButton,
  MenuContent,
  CategoryGrid,
  MenuFooter,
  UserAvatar,
  UserInfo,
  UserName,
  UserRole,
  VersionText,
  LogoutButton
} from './styles';
import { invalidateCache } from '../../containers/Navbar';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import { isMobileViewport } from '../../constants/responsive';

// Logos
import logoLadoBom from '../../resources/imgs/logo-lado-bom-seguros.svg';
import logorv from '../../resources/imgs/logo.png';

const mapState = (state: iRootState) => ({
  currentPage: state.currentPage,
  currentToggledMenu: state.currentToggledMenu,
  user: state.user,
  availableBrokersAndEstates: state.availableBrokersAndEstates
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateCurrentPage: (state: CurrentPageState) =>
    dispatch.currentPage.updateCurrentPage(state),
  updateCurrentToggledMenu: (state: CurrentToggledMenuState) =>
    dispatch.currentToggledMenu.updateCurrentToggledMenu(state)
});

interface MobileMenuOwnProps {
  isOpen: boolean;
  onClose: () => void;
}

type Props = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch> &
  MobileMenuOwnProps;

const MobileMenu: React.FC<Props> = ({
  isOpen,
  onClose,
  currentPage,
  user,
  availableBrokersAndEstates,
  updateCurrentPage,
  updateCurrentToggledMenu
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<MenuItemProps | null>(null);

  const history = useHistory();
  const match = useRouteMatch();

  // Filtra opções de menu baseado no role do usuário
  const filteredMenuOptions = useMemo(() => {
    let options = [...menuOptions];

    if (
      user.role === UserRolesEnum.franchiseAdmin ||
      user.role === UserRolesEnum.multiFranchiseAdmin
    ) {
      options = options.filter(menu => menu.alias !== 'MANAGEMENT');
    }

    if (!availableBrokersAndEstates.enableFireInsuranceMenu) {
      options = options.filter(menu => menu.alias !== 'FIRE');
    }

    const isAdminUser =
      user.role === UserRolesEnum.brokerAdmin ||
      user.role === UserRolesEnum.brokerAnalyst;
    if (
      !availableBrokersAndEstates.lenderInsuranceEnabled &&
      !isAdminUser &&
      user.role !== UserRolesEnum.estateQuotation
    ) {
      options = options.filter(menu => menu.alias !== 'LENDER_INSURANCE');
    }

    options = options.map(menu => {
      if (!menu.submenuItems) return menu;

      let submenuItems = menu.submenuItems.filter(item => {
        if ((item as any).requiresRoles?.length) {
          return (item as any).requiresRoles.includes(user.role);
        }
        if ((item as any).requiresRole) {
          return user.role === (item as any).requiresRole;
        }
        return true;
      });

      if (user.role === UserRolesEnum.estateQuotation) {
        const allowedSubmenus = [
          'fire-insurance-budget',
          'fire-insurance-budgets',
          'find-record',
          'bond-insurance',
          'bond-insurances',
          'capitalizations',
          'lender-insurances',
          'claim-estates'
        ];

        submenuItems = submenuItems.filter(item =>
          allowedSubmenus.includes(item.name)
        );
      }

      if (menu.name === 'MANAGEMENT' && user.role?.includes('ESTATE')) {
        submenuItems = submenuItems.filter(sub => {
          if (sub.name === 'REPORTS') return true;
          if (sub.name === 'ESTATE_UNITS') {
            return user.role === UserRolesEnum.estateAdmin;
          }
          if (sub.name === 'STANDALONE_CHARGES') {
            return Boolean(availableBrokersAndEstates.standaloneChargesEnabled);
          }
          return false;
        });
      }

      return { ...menu, submenuItems };
    });

    return options;
  }, [
    user.role,
    availableBrokersAndEstates.enableFireInsuranceMenu,
    availableBrokersAndEstates.lenderInsuranceEnabled,
    availableBrokersAndEstates.standaloneChargesEnabled
  ]);

  // Reset submenu quando fecha
  useEffect(() => {
    if (!isOpen) {
      setSelectedCategory(null);
      setIsClosing(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setSelectedCategory(null);
    }, 280);
  };

  const handleCategoryClick = (category: MenuItemProps) => {
    const hasSubmenu = category.submenuItems && category.submenuItems.length > 0;

    if (hasSubmenu) {
      setSelectedCategory(category);
    } else {
      // Navegação direta (ex: Dashboard)
      navigateToPage(category);
    }
  };

  const navigateToPage = (item: MenuItemProps) => {
    updateCurrentPage({
      id: item.id,
      name: item.alias || item.name,
      text: item.text,
      title: item.title
    });
    updateCurrentToggledMenu({});

    if (item.route) {
      history.push(`${match.url}/${item.route}`);
    }

    handleClose();
  };

  const navigateToSubmenu = (item: SubMenuItemProps, parent: MenuItemProps) => {
    updateCurrentPage({
      id: item.id,
      name: item.name,
      text: item.text,
      title: item.title,
      parent: {
        id: parent.id,
        name: parent.alias || parent.name
      }
    });
    updateCurrentToggledMenu({});

    if (item.name === 'bond-insurance' && isMobileViewport(window.innerWidth)) {
      history.push('/pwa/new-analysis');
    } else if (item.route) {
      history.push(`${match.url}/${item.route}`);
    }

    handleClose();
  };

  const getUserInitials = (): string => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  const getUserRoleLabel = (): string => {
    if (user.role?.includes('ADMIN')) return 'Administrador';
    if (user.role?.includes('BROKER')) return 'Corretor';
    if (user.role?.includes('ESTATE')) return 'Imobiliária';
    return 'Usuário';
  };

  if (!isOpen && !isClosing) return null;

  return (
    <>
      <Overlay isClosing={isClosing} onClick={handleClose} />
      <MenuContainer isClosing={isClosing}>
        {/* Header */}
        <MenuHeader>
          <CloseButton onClick={handleClose}>
            <Icon name="close" />
          </CloseButton>
          <LogoContainer>
            <Image src={logoLadoBom} alt="Logo" />
          </LogoContainer>
          {/* Espaço para manter o logo centralizado */}
          <div style={{ width: 44 }} />
        </MenuHeader>

        {/* Content */}
        <MenuContent>
          <CategoryGrid>
            {filteredMenuOptions.map((category) => {
              const hasSubmenu = Boolean(category.submenuItems && category.submenuItems.length > 0);
              const isActive = Boolean(
                currentPage?.name === category.alias ||
                currentPage?.parent?.name === category.alias
              );

              return (
                <MobileMenuCategory
                  key={category.id}
                  id={category.id}
                  icon={category.icon}
                  text={category.text}
                  isActive={isActive}
                  hasSubmenu={hasSubmenu}
                  onClick={() => handleCategoryClick(category as MenuItemProps)}
                />
              );
            })}
          </CategoryGrid>
        </MenuContent>

        {/* Footer com info do usuário */}
        <MenuFooter>
          <UserAvatar>{getUserInitials()}</UserAvatar>
          <UserInfo>
            <UserName>{user?.name || 'Usuário'}</UserName>
            <UserRole>{getUserRoleLabel()}</UserRole>
          </UserInfo>
          <LogoutButton onClick={async () => await invalidateCache()}>
            <Icon name="sign-out" />
          </LogoutButton>
        </MenuFooter>

        <VersionText>v{process.env.REACT_APP_VERSION || '1.0.0'}</VersionText>

        {/* Submenu (renderiza por cima quando ativo) */}
        {selectedCategory && (
          <MobileSubmenu
            category={selectedCategory}
            currentPage={currentPage}
            onBack={() => setSelectedCategory(null)}
            onNavigate={(item) => navigateToSubmenu(item, selectedCategory)}
          />
        )}
      </MenuContainer>
    </>
  );
};

export default connect(mapState, mapDispatch)(MobileMenu);
